import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, CircleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { formatUSD, formatUSDRange, loadJourneyResult, saveJourneyResult } from '@/lib/journeyBuilder';
import BackToApp from '@/components/BackToApp';
import '@/components/care-page.css';

const ADD_ONS = [
  {
    id: 'hotel-upgrade',
    label: 'Upgrade lodging one tier',
    description: 'Move from extended-stay to a hotel nearer campus, or from mid-range to a 4-star medical-center hotel.',
    extraCost: 0.18,
  },
  {
    id: 'second-caregiver',
    label: 'Second caregiver airfare',
    description: 'One additional round-trip domestic ticket, typical coach fare.',
    extraCost: 420,
  },
  {
    id: 'ground-transfer',
    label: 'Airport-to-campus car service',
    description: 'Private transfer on arrival and departure, not rideshare.',
    extraCost: 180,
  },
  {
    id: 'concierge',
    label: 'Priority records + appointment concierge',
    description: 'ByOnco coordinator pushes records, prior auth, and first-week calendar within 72 hours.',
    extraCost: 450,
  },
];

export default function JourneyPlanDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const stored = loadJourneyResult();
  const bundle = location.state || stored;
  const plan = bundle?.selectedPlan || bundle?.plan;
  const intake = bundle?.intake;

  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');
  const [reportFiles, setReportFiles] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [liveQuote, setLiveQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  const addOnTotal = useMemo(() => selectedAddOns.reduce((sum, id) => {
    const addOn = ADD_ONS.find((row) => row.id === id);
    if (!addOn) return sum;
    if (addOn.extraCost < 1) return sum + Math.round((plan?.priceRangeMin || 0) * addOn.extraCost);
    return sum + addOn.extraCost;
  }, 0), [selectedAddOns, plan]);

  if (!plan) {
    return (
      <div className="care-page min-h-screen bg-cream-paper px-4 py-16 text-center">
        <p className="text-forest-ink mb-4">Choose a plan from Journey Builder first.</p>
        <Button onClick={() => navigate('/journey-builder')} className="rounded-full bg-forest-ink text-cream-paper">
          Back to Journey Builder
        </Button>
      </div>
    );
  }

  const finalMin = (plan.priceRangeMin || 0) + addOnTotal;
  const finalMax = (plan.priceRangeMax || 0) + addOnTotal;

  const toggleAddOn = (id) => {
    setSelectedAddOns((prev) => (prev.includes(id) ? prev.filter((row) => row !== id) : [...prev, id]));
  };

  const runLiveQuote = async () => {
    setQuoteLoading(true);
    setQuoteError('');
    try {
      const res = await fetch(apiUrl('/api/care-marketplace/package-quote'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('byonco_jwt')
            ? { Authorization: `Bearer ${localStorage.getItem('byonco_jwt')}` }
            : {}),
        },
        body: JSON.stringify({
          hospital_id: plan.hospitalId,
          hospital_name: plan.hospitalName,
          city: plan.city,
          state: plan.state,
          cancer_type: intake?.cancerType,
          insurance: intake?.insurance || 'employer_ppo',
          member_id: memberId || undefined,
          lodging_cost: plan.lodgingCost || 0,
          travel_min: 0,
          travel_max: 0,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Live quote failed, subscription may be required.');
      }
      setLiveQuote(await res.json());
    } catch (err) {
      setQuoteError(err.message || 'Live quote failed');
    } finally {
      setQuoteLoading(false);
    }
  };

  const requestPlan = async () => {
    setSubmitting(true);
    setError('');
    const reference = `BYO-${new Date().getFullYear()}-${Math.random().toString(36).slice(2, 9).toUpperCase()}`;
    let sent = { reference, wait: 'within 48 hours' };
    try {
      const body = new FormData();
      body.append('planType', plan.planType.toLowerCase());
      body.append('hospitalName', plan.hospitalName);
      body.append('city', plan.city);
      body.append('country', 'United States');
      body.append('priceRangeMin', String(finalMin));
      body.append('priceRangeMax', String(finalMax));
      body.append('currency', 'USD');
      body.append('treatmentDuration', plan.duration || '');
      body.append('addOns', JSON.stringify(selectedAddOns));
      if (bundle?.conversationId) body.append('conversationId', bundle.conversationId);
      for (const file of reportFiles) {
        body.append('reports', file);
      }

      const res = await fetch(apiUrl('/api/journey-builder/bookings'), {
        method: 'POST',
        credentials: 'include',
        headers: localStorage.getItem('byonco_jwt')
          ? { Authorization: `Bearer ${localStorage.getItem('byonco_jwt')}` }
          : {},
        body,
      });
      if (res.ok) {
        const data = await res.json();
        sent = {
          reference: data.bookingReference || reference,
          wait: data.estimatedResponseTime || 'within 48 hours',
          reports: (data.uploadedReports || []).length,
        };
      }
    } catch {
      setError('We saved this request on your device. If the team does not write within two business days, reply from your account email.');
    } finally {
      setSubmitted(sent);
      setSubmitting(false);
      saveJourneyResult({ ...bundle, selectedPlan: plan, requestToken: sent.reference });
    }
  };

  return (
    <div className="care-page min-h-screen bg-cream-paper">
      <BackToApp />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => navigate('/journey-builder')}
          className="inline-flex items-center gap-2 text-sm text-forest-ink mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to three plans
        </button>

        <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-mist text-forest-ink border border-hair">
                Care package · {plan.planType}
              </span>
              <h1 className="text-2xl font-semibold text-forest-ink mt-3">{plan.hospitalName}</h1>
              <p className="text-sm text-charcoal mt-1">{plan.city}, {plan.state} · {plan.designation}</p>
              <p className="text-sm text-charcoal mt-3 max-w-2xl">{plan.hospitalNote}</p>
            </div>
            <div className="md:text-right">
              <p className="text-xs uppercase tracking-wide text-charcoal">Estimated package band</p>
              <p className="text-2xl font-semibold text-forest-ink mt-1">{formatUSDRange(finalMin, finalMax)}</p>
              <p className="text-xs text-charcoal mt-1">{plan.duration} · USD · planning estimate</p>
            </div>
          </div>
        </div>

        {intake ? (
          <div className="rounded-3xl border border-hair bg-mist/70 p-5 mb-5 text-sm text-forest-ink">
            {intake.city}, {intake.state} · {plan.protocol}
          </div>
        ) : null}

        <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
          <h2 className="text-lg font-semibold text-forest-ink mb-4">What this care package includes</h2>
          <ul className="space-y-3 mb-5">
            {(plan.packageInclusions || [
              'Treatment estimate band for the modeled pathway (your share + typical allowed amounts)',
              'Lodging assumptions for the treatment window',
              'Travel / transfer estimate for patient (and caregiver when selected)',
              'ByOnco coordinator handoff after you request this package, no payment collected here',
            ]).map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-forest-ink">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-semibold text-forest-ink mb-3">Pathway details</h3>
          <ul className="space-y-3">
            {(plan.bullets || []).map((bullet) => (
              <li key={bullet} className="flex items-start gap-3 text-sm text-forest-ink">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        {plan.timeline?.length ? (
          <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
            <h2 className="text-lg font-semibold text-forest-ink mb-4">Week by week</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plan.timeline.map((block) => (
                <div key={block.week} className="rounded-2xl border border-hair bg-mist/50 p-4">
                  <p className="text-xs uppercase tracking-wide text-charcoal">{block.week}</p>
                  <p className="font-semibold text-forest-ink mt-1">{block.phase}</p>
                  <ul className="mt-2 space-y-1">
                    {block.tasks.map((task) => (
                      <li key={task} className="text-sm text-charcoal">{task}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
          <h2 className="text-lg font-semibold text-forest-ink mb-4">Optional add-ons (USD)</h2>
          <div className="space-y-3">
            {ADD_ONS.map((addOn) => {
              const selected = selectedAddOns.includes(addOn.id);
              const cost = addOn.extraCost < 1
                ? `+${Math.round(addOn.extraCost * 100)}% of your share`
                : `+${formatUSD(addOn.extraCost)}`;
              return (
                <label
                  key={addOn.id}
                  className={`flex items-start gap-3 p-4 border rounded-2xl cursor-pointer ${
                    selected ? 'border-forest-ink bg-mist' : 'border-hair'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleAddOn(addOn.id)}
                    className="mt-1 w-4 h-4 accent-[#0f3e17]"
                  />
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-medium text-forest-ink">{addOn.label}</span>
                      <span className="text-sm text-forest-ink">{cost}</span>
                    </span>
                    <span className="block text-xs text-charcoal mt-1">{addOn.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
          <h2 className="text-lg font-semibold text-forest-ink mb-2">Live quote & insurance eligibility</h2>
          <p className="text-sm text-charcoal mb-4">
            Runs a CMS price-transparency quote plus X12 270/271-style eligibility (Stedi/Availity when API keys are configured).
            Capacity shows new-patient scheduling windows, not inpatient bed counts.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              className="flex-1 h-12 rounded-2xl border border-hair bg-mist px-3 text-forest-ink text-sm"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="Member ID for eligibility check"
              autoComplete="off"
            />
            <Button
              type="button"
              onClick={runLiveQuote}
              disabled={quoteLoading}
              className="h-12 px-5 rounded-full bg-forest-ink text-cream-paper"
            >
              {quoteLoading ? 'Checking…' : 'Get live quote'}
            </Button>
          </div>
          {quoteError ? <p className="text-sm text-[#8a1f1f] mb-3">{quoteError}</p> : null}
          {liveQuote?.packageTotal ? (
            <div className="rounded-2xl border border-hair bg-mist/60 p-4 text-sm text-forest-ink space-y-2">
              <p>
                Patient medical share:{' '}
                <strong>
                  {formatUSDRange(
                    liveQuote.packageTotal.medicalPatientShareMin,
                    liveQuote.packageTotal.medicalPatientShareMax,
                  )}
                </strong>
                {liveQuote.packageTotal.inNetwork != null
                  ? ` · ${liveQuote.packageTotal.inNetwork ? 'In-network' : 'Out-of-network'}`
                  : ''}
              </p>
              <p>
                Coverage: {liveQuote.packageTotal.coverageStatus || 'n/a'}
                {liveQuote.eligibility?.planName ? ` · ${liveQuote.eligibility.planName}` : ''}
              </p>
              {liveQuote.packageTotal.waitDays != null ? (
                <p>
                  New-patient wait ~{liveQuote.packageTotal.waitDays} days
                  {liveQuote.packageTotal.nextSlotAt
                    ? ` · next slot ${new Date(liveQuote.packageTotal.nextSlotAt).toLocaleDateString()}`
                    : ''}
                </p>
              ) : null}
              <p className="text-xs text-charcoal">{liveQuote.fulfillment?.note}</p>
            </div>
          ) : plan.liveQuote?.packageTotal ? (
            <div className="rounded-2xl border border-hair bg-mist/60 p-4 text-sm text-forest-ink">
              Package already enriched from Journey Builder live marketplace.
              Coverage {plan.capacity?.coverageStatus || 'checked'}; wait ~{plan.capacity?.waitDays ?? '-'} days.
            </div>
          ) : null}
        </div>

        <div className="rounded-3xl border border-hair bg-paper p-6 mb-5">
          <h2 className="text-lg font-semibold text-forest-ink mb-4">Cost breakdown</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-charcoal">Your medical share</dt><dd className="text-forest-ink">{plan.patientOOP}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-charcoal">Typical insurer allowed</dt><dd className="text-forest-ink">{plan.medicalAllowed}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-charcoal">Travel</dt><dd className="text-forest-ink">{plan.travelCost}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-charcoal">Lodging</dt><dd className="text-forest-ink">{plan.lodgingCost ? formatUSD(plan.lodgingCost) : 'None'}</dd></div>
            {addOnTotal > 0 ? (
              <div className="flex justify-between gap-3"><dt className="text-charcoal">Add-ons</dt><dd className="text-forest-ink">+{formatUSD(addOnTotal)}</dd></div>
            ) : null}
            <div className="flex justify-between gap-3 border-t border-hair pt-3 mt-3">
              <dt className="font-semibold text-forest-ink">Estimated total to you</dt>
              <dd className="text-lg font-semibold text-forest-ink">{formatUSDRange(finalMin, finalMax)}</dd>
            </div>
          </dl>
          {plan.costBreakdown ? (
            <p className="text-xs text-charcoal mt-3">
              Hospital facility {plan.costBreakdown.hospitalFees} · drugs/infusion {plan.costBreakdown.drugsAndInfusion} · diagnostics {plan.costBreakdown.diagnostics}. Those are allowed amounts, not your copay.
            </p>
          ) : null}
          <p className="text-sm text-forest-ink mt-4 leading-relaxed rounded-2xl border border-hair bg-mist/70 px-4 py-3">
            These are not final numbers, only estimates from published market data. Once you complete your preferences and request this plan, we will reach out within 48 hours with exact figures from the hospital.
          </p>
        </div>

        <div className="rounded-3xl border border-hair bg-mist p-6 mb-6">
          <h2 className="text-lg font-semibold text-forest-ink mb-2">Request this care package</h2>
          <p className="text-sm text-charcoal mb-4">
            No payment is taken here. A ByOnco coordinator confirms insurance, appointments, and lodging, then sends written numbers from the hospital. Save your request reference, it also goes to your account email when email delivery is enabled.
          </p>
          {submitted ? (
            <div className="rounded-2xl border border-hair bg-paper p-4">
              <p className="text-sm text-charcoal">Request reference</p>
              <p className="text-lg font-mono font-semibold text-forest-ink mt-1">{submitted.reference}</p>
              <p className="text-sm text-charcoal mt-2">
                We will reach out within 48 hours with exact numbers once your preferences are reviewed.
                {submitted.reports ? ` ${submitted.reports} report file(s) attached.` : ''}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  type="button"
                  onClick={() => navigate('/teleconsultation')}
                  className="h-10 px-4 rounded-full bg-forest-ink text-cream-paper"
                >
                  Book a video consult
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/second-opinion')}
                  className="h-10 px-4 rounded-full border-hair text-forest-ink"
                >
                  Get an case brief first
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/get-matched')}
                  className="h-10 px-4 rounded-full border-hair text-forest-ink"
                >
                  Match more centres
                </Button>
              </div>
              <p className="text-sm text-forest-ink mt-3 leading-relaxed">
                These figures are planning estimates. Confirm the final numbers with the hospital and your insurer.
              </p>
              {error ? <p className="text-xs text-charcoal mt-2">{error}</p> : null}
            </div>
          ) : (
            <div className="space-y-4">
              <label className="block text-left">
                <span className="text-sm font-medium text-forest-ink">Upload reports (optional)</span>
                <p className="text-xs text-charcoal mt-1 mb-2">
                  Pathology, imaging summaries, or discharge notes, PDF or images, up to 12 MB each.
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx"
                  className="block w-full text-sm text-charcoal file:mr-3 file:rounded-full file:border-0 file:bg-forest-ink file:px-4 file:py-2 file:text-cream-paper"
                  onChange={(e) => setReportFiles(Array.from(e.target.files || []))}
                />
                {reportFiles.length ? (
                  <p className="text-xs text-charcoal mt-2">{reportFiles.length} file(s) ready to send</p>
                ) : null}
              </label>
              <Button
                type="button"
                onClick={requestPlan}
                disabled={submitting}
                className="h-12 px-6 rounded-full bg-forest-ink text-cream-paper hover:bg-forest-shadow"
              >
                {submitting ? 'Sending…' : 'Request this package'}
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 text-xs text-charcoal">
          <CircleAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            ByOnco does not bill like a hospital and does not replace your oncologist. Confirm network status, prior authorization, and charity-care policies with {plan.hospitalName} before you travel.
          </p>
        </div>
      </div>
    </div>
  );
}
